import { notification } from 'antd';
import SettingsForm from '../Shared/SettingsForm';
import SaveButton from './SaveButton';
import Connection from '../Shared/Connection';
import React, { useState } from 'react';
import {
  AnyAssetApplication,
  APIAsset,
  Connections,
  ConnectionTypes,
  Flex,
  FormikAPIFormValues,
  FormikFormConnectionKey,
  getCustomFieldsSchema,
  Grid,
  Loading,
  NONE_SELECT,
  openNotificationWithIcon,
  PageContent,
  useAssetConfig,
  useAssetParams,
  useAssetQuery,
  useAssetTranslation,
  useHasAssetPermissions,
} from '@mss/mss-component-library';
import { Formik } from 'formik';
import TimeAgo from 'react-timeago';
import {
  assetModel,
  fullAssetName,
  paramsToExportAssetType,
} from '../Import/util';
import { Form } from 'formik-antd/lib/form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { array, object } from 'yup';
import { useCustomFieldsQuery } from '../../../asset/services/queries';
import { patchAsset, putAsset } from '../services/update';
import { useQueryClient } from '@tanstack/react-query';
import { emptyConnectionTest } from '../utils/emptyConnectionTest';

export default function Settings<AssetType extends AnyAssetApplication>() {
  const queryClient = useQueryClient();
  const { data: apiAsset, isLoading, refetch } = useAssetQuery();
  const assetParams = useAssetParams();
  const { assetSubType, assetType } = assetParams;
  const { matchingConfig } = useAssetConfig(assetSubType);
  const { t } = useAssetTranslation();
  const permissions = useHasAssetPermissions();
  const { data: customFields, isLoading: isLoadingCustomFields } =
    useCustomFieldsQuery(assetModel[paramsToExportAssetType(assetType)]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  function successNotification(asset: Partial<APIAsset>, autoSave?: boolean) {
    openNotificationWithIcon(
      notification.success,
      t('((assetName)) was updated successfully.', {
        assetName: asset.name,
      }),
      autoSave
        ? t(
            'The credentials were automatically saved, due to the succesful connection test'
          )
        : ''
    );
  }

  function failedNotification() {
    openNotificationWithIcon(
      notification.error,
      t('((assetName)) could not be updated', {
        assetName:
          apiAsset?.name ||
          t(fullAssetName(paramsToExportAssetType(assetType))),
      }),
      ''
    );
  }

  async function handleSubmit(
    values: FormikAPIFormValues<AssetType>,
    autoSave?: boolean
  ) {
    const baseAsset = values.assets[0].values;
    const asset: Partial<AssetType> = !autoSave
      ? baseAsset
      : Object.keys(baseAsset).reduce((acc, key) => {
          const connectionTypes = Object.keys(
            matchingConfig.form.connect
          ) as ConnectionTypes[];
          const connectValueKeys = connectionTypes
            .map(
              x =>
                matchingConfig.form.connect[x]?.testConnection.valueKeys || []
            )
            .flat();
          if (connectValueKeys.includes(key)) {
            return {
              ...acc,
              [key]: baseAsset[key as keyof AssetType],
            };
          }
          return acc;
        }, {} as Partial<AssetType>);

    try {
      const updater =
        matchingConfig.form.method === 'put' ? putAsset : patchAsset;
      const data = { ...baseAsset, ...asset };
      const response = await updater(
        assetParams,
        matchingConfig.form.submitDataParser?.(data) || data
      );

      if (!response.ok) throw new Error('Something went wrong');
      await queryClient.invalidateQueries({
        queryKey: ['list', assetType],
      });
      successNotification(baseAsset, autoSave);
      await refetch();
    } catch {
      failedNotification();
    }
  }

  const schemas = object()
    .concat(matchingConfig.form.configure.schema)
    .concat(object(getCustomFieldsSchema(t, customFields)));

  return (
    <PageContent title={t('Settings')}>
      {(!apiAsset || isLoading || isLoadingCustomFields) && <Loading />}
      {apiAsset && (
        <Formik<FormikAPIFormValues<AssetType>>
          enableReinitialize
          initialValues={{
            assets: Array.from({ length: 1 }, _ => {
              const base = {
                values: apiAsset,
              };

              return Object.keys(matchingConfig.form.connect).reduce(
                (acc, key) => {
                  return {
                    ...acc,
                    [`test_${key}`]: emptyConnectionTest,
                  };
                },
                base as any
              );
            }),
          }}
          onSubmit={values => handleSubmit(values)}
          validateOnMount
          validationSchema={object().shape({
            assets: array().of(object().shape({ values: schemas })),
          })}
        >
          {formikProps => {
            const { values, initialValues } = formikProps;
            const currentAsset = values.assets[0].values;
            const IPChanged =
              values.assets[0].values.ip_address !==
              initialValues.assets[0].values.ip_address;

            const connectionTypes = Object.keys(
              matchingConfig.form.connect
            ) as [keyof Connections];

            const alwaysOnConfigs = connectionTypes.filter(
              x => !matchingConfig.form.connect[x]?.select
            );
            const selectableConfigs = connectionTypes.filter(
              x => !!matchingConfig.form.connect[x]?.select
            );
            const firstSelectableConfig =
              matchingConfig.form.connect[selectableConfigs[0]]?.select;

            const { key: connectionSelectKey } = firstSelectableConfig ?? {};

            const selectedConnectionType =
              connectionSelectKey &&
              values.assets[0].values[connectionSelectKey as keyof AssetType];

            const relevantConnectionTypes = connectionTypes.filter(x =>
              [...alwaysOnConfigs, selectedConnectionType].includes(x)
            ) as [keyof Connections];

            const initialConnectionType =
              connectionSelectKey &&
              initialValues.assets[0].values[
                connectionSelectKey as keyof AssetType
              ];
            const connectionTypeChanged =
              initialConnectionType !== selectedConnectionType;

            const credentialsChanged = relevantConnectionTypes.filter(key =>
              matchingConfig.form.connect[
                key as keyof Connections
              ]?.testConnection.valueKeys.some(
                (x: string) =>
                  apiAsset[x as keyof AnyAssetApplication] !==
                  currentAsset[x as keyof AssetType]
              )
            );
            const changedConnectionTestNotRun = credentialsChanged.some(
              key =>
                values.assets[0][`test_${key}` as FormikFormConnectionKey]
                  ?.result.status.port == null
            );

            const anyTestNotRun = relevantConnectionTypes.some(
              key =>
                values.assets[0][`test_${key}` as FormikFormConnectionKey]
                  ?.result.status.port == null
            );

            const changedTypeNotRan =
              selectedConnectionType !== NONE_SELECT &&
              values.assets[0][
                `test_${selectedConnectionType}` as FormikFormConnectionKey
              ]?.result.status.port == null;

            const testHasErrors = relevantConnectionTypes.some(key =>
              Object.values(
                values.assets[0][`test_${key}` as FormikFormConnectionKey]
                  ?.result.status
              ).includes(false)
            );

            const testConditionMap = {
              connectionTypeChanged: connectionTypeChanged && changedTypeNotRan,
              ipChanged: IPChanged && anyTestNotRun,
              credentialsChanged:
                credentialsChanged && changedConnectionTestNotRun,
            };

            const shouldTest = Object.values(testConditionMap).some(Boolean);

            return (
              <Form>
                <SettingsForm disabled={isSaving || !permissions.change} />
                <Grid.Row className="-mt-2">
                  <Grid.Col width={8}>
                    {shouldTest && (
                      <Flex
                        v="center"
                        className="h-full text-danger text-xs"
                        data-ta="shouldRetestError"
                      >
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          size="1x"
                          className="ml-2 mr-4"
                          color="#fa4654"
                        />
                        {t(
                          `When changing connection related settings, ${selectableConfigs.length > 0 ? 'every' : 'the'} connection has to be tested before saving.`
                        )}
                      </Flex>
                    )}
                  </Grid.Col>

                  <Grid.Col width={4}>
                    <Flex
                      h="end"
                      v="center"
                      style={{ marginTop: '1rem', marginBottom: '1rem' }}
                    >
                      <small className="mr-4 text-gray-500">
                        {`${t('Last updated')}: `}
                        <strong>
                          {apiAsset.updated_at ? (
                            <TimeAgo date={apiAsset.updated_at} />
                          ) : (
                            t('unknown')
                          )}
                        </strong>
                      </small>
                      <SaveButton
                        testHasErrors={testHasErrors}
                        shouldTest={shouldTest}
                      />
                    </Flex>
                  </Grid.Col>
                </Grid.Row>
                {Object.keys(matchingConfig.form.connect).length > 0 && (
                  <Grid.Row>
                    <Grid.Col width={12}>
                      <Connection
                        autoSave={async () => {
                          if (testConditionMap.connectionTypeChanged) {
                            return;
                          }
                          setIsSaving(true);
                          formikProps.setSubmitting(true);
                          await handleSubmit(values, true);
                          formikProps.setSubmitting(false);
                          setIsSaving(false);
                        }}
                        compact
                        disabled={!permissions.change || isSaving}
                      />
                    </Grid.Col>
                  </Grid.Row>
                )}
              </Form>
            );
          }}
        </Formik>
      )}
    </PageContent>
  );
}
